import React, { useEffect, useState, useCallback } from 'react';
import PageSizeSelector from '../Common/PageSizeSelector';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Swal from "sweetalert2";
import moment from 'moment';

import MessageThread from './components/MessageThread';
import BankVerificationGridView from './bankVerificationComponents/BankVerificationGridView';
import BankVerificationListView from './bankVerificationComponents/BankVerificationListView';
import ViewToggle from '../Common/ViewToggle';
import NoDataFound from '../Common/NoDataFound';
import TableFooter from '../Common/TableFooter';
import CommonExportButtons from '../Common/CommonExportButtons';
import VerificationFilter from '../Common/VerificationFilter';

import { getPropertyBankVerifyListsAction, updatePropertyBankVerifyAction, getPropertyBankVerifyMessagessAction, createPropertyBankVerifyMessageAction } from '../../../store/actions/propertyBankVerification/PropertyBankVerificationActions';

const BankVerficationList = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const isPending = location.pathname.includes('pending-bank-verification-list');
	const isCompleted = location.pathname.includes('bank-completed-list');

	// Reset currentPage, filters, and sorting when path changes
	useEffect(() => {
		setCurrentPage(1);
		// Clear all filter states
		setStartDate(null);
		setEndDate(null);
		setCustomer(null);
		setAppliedStartDate(null);
		setAppliedEndDate(null);
		setAppliedCustomer(null);
		// Clear sorting states
		setSortField('');
		setSortDirection('desc');
		// Close filter menu
		setOpenMenu(true);
	}, [location.pathname]);

	const { auth } = useSelector((state) => state.auth);
	const UserBankId = auth?.user?.bankId ? auth?.user?.bankId : null
	const UserId = auth?.user?._id ? auth?.user?._id : null
	const { propertyBankVerifyLists, propertyBankVerifyMessages, loading } = useSelector((state) => state.propertyBankVerification);
	const propertyBankVerifyListRows = propertyBankVerifyLists?.rows || [];
	const propertyBankVerifyMessageRows = propertyBankVerifyMessages?.rows || [];
	const totalPages = propertyBankVerifyLists?.pages || 1;
	const [show, setShow] = useState(false);
	const [verifyId, setVerifyId] = useState('');
	const [vStatus, setVsatatus] = useState(false)

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(12);
	const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
	const [sortField, setSortField] = useState('');
	const [sortDirection, setSortDirection] = useState('desc');

	// Filter states
	const [openMenu, setOpenMenu] = useState(true);
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);
	const [customer, setCustomer] = useState(null);

	// Applied filter states (only set when search button is clicked)
	const [appliedStartDate, setAppliedStartDate] = useState(null);
	const [appliedEndDate, setAppliedEndDate] = useState(null);
	const [appliedCustomer, setAppliedCustomer] = useState(null);

	const handleStatusChange = async (id, status) => {
		let bText = "";
		let aText = "";
		let query = ""
		if(status === 'pickedup') {
			bText = "Do you want to pick up this property verification!"
			aText = "Picked up Successfully."
			query = 'populate=messages,propertyId,userId&filter_status=pending&filter_bankId='+UserBankId+'&orderBy=createdAt'
		} else if(status === 'verified') {
			bText = "Do you want to make this property as verified!"
			aText = "Marked as verified up Successfully."
			query = 'populate=messages,propertyId,userId&filter_status=pickedup&filter_bankId='+UserBankId+'&filter_agentId='+UserId+'&orderBy=createdAt'
		} else {
			bText = "Do you want to make this property as rejected!"
			aText = "Marked as rejected Successfully."
			query = 'populate=messages,propertyId,userId&filter_status=pickedup&filter_bankId='+UserBankId+'&filter_agentId='+UserId+'&orderBy=createdAt'
		}
		const result = await Swal.fire({
			title: "Are you sure?",
			text: bText,
			icon: "warning",
			showCancelButton: true,
			confirmButtonColor: "#3085d6",
			cancelButtonColor: "#d33",
			confirmButtonText: "Yes!"
		});

		if (result.isConfirmed) {
			const payload = {
				"status":status
			}
			const response = await dispatch(updatePropertyBankVerifyAction(id,payload));
			if (response.data.success) {
				Swal.fire("Success!", aText, "success");
				dispatch(getPropertyBankVerifyListsAction(currentPage, pageSize, '', query));
			} else {
				Swal.fire({ icon: 'error', title: 'Error', text: response.data?.msg || 'Something went wrong' });
			}
		}
	};

	const handleViewChange = (newViewMode) => {
		setViewMode(newViewMode);
	};

	const handleSort = (field) => {
		if (sortField === field) {
			setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
		} else {
			setSortField(field);
			setSortDirection('desc');
		}
		setCurrentPage(1); // Reset to first page when sorting
	};

	const buildFilterQuery = useCallback(() => {
		const params = new URLSearchParams(location.search);
		const id = params.get('id');
		let idFilter = id ? `&filter__id=${id}` : '';
		
		// Build base query with population
		let baseQuery = 'populate=messages,propertyId,userId';
		
		// Add status filter based on page type
		if(isPending) {
			baseQuery += `&filter_status=pending&filter_bankId=${UserBankId}`;
		} else if(isCompleted) { 
			baseQuery += `&filter_status=in-verified,rejected&filter_bankId=${UserBankId}&filter_agentId=${UserId}`;
		} else {
			baseQuery += `&filter_status=pickedup&filter_bankId=${UserBankId}&filter_agentId=${UserId}`;
		}
		
		// Add date range filter (only if applied)
		if (appliedStartDate && appliedEndDate) {
			const start = moment(appliedStartDate).format('YYYY/MM/DD');
			const end = moment(appliedEndDate).format('YYYY/MM/DD');
			baseQuery += `&filter_createdAt=btw-${start},${end}`;
		}
		
		// Add customer filter (only if applied)
		if (appliedCustomer) {
			baseQuery += `&filter_userId=${appliedCustomer}`;
		}
		
		// Add sorting parameters
		if (sortField) {
			baseQuery += `&orderBy=${sortField}&orderByDir=${sortDirection}`;
		} else {
			baseQuery += '&orderBy=createdAt'; // Default sort by createdAt
		}
		
		return baseQuery + idFilter;
	}, [location.search, isPending, isCompleted, UserBankId, UserId, appliedStartDate, appliedEndDate, appliedCustomer, sortField, sortDirection]);

	const setDateRange = ({ startDate: start, endDate: end }) => {
		setStartDate(start);
		setEndDate(end);
	};

	const onSubmit = async () => {
		// Set applied filter states
		setAppliedStartDate(startDate);
		setAppliedEndDate(endDate);
		setAppliedCustomer(customer);
		setCurrentPage(1);
		
		// The useEffect will trigger with new applied filter states
	};

	const isFilterActive = appliedStartDate || appliedEndDate || appliedCustomer;

	const handleClearFilter = () => {
		// Clear both current and applied filter states
		setStartDate(null);
		setEndDate(null);
		setCustomer(null);
		setAppliedStartDate(null);
		setAppliedEndDate(null);
		setAppliedCustomer(null);
		setCurrentPage(1);
		// The useEffect will trigger with cleared applied filter states
	};

	const handleSend = async (msg) => {
		const payload = {
			"bankVerificationId": verifyId,
			"senderType": "agent",
			"message": msg
		  }
		const response = await dispatch(createPropertyBankVerifyMessageAction(payload));
		if (response.data.success) {
			dispatch(getPropertyBankVerifyMessagessAction('filter_bankVerificationId='+verifyId,false));
			Swal.fire("Success!", "Message send successfully", "success");
		} else {
			Swal.fire("Failed!", "Something went wrong.", "warning");
		}
	};

	const viewDocument = (data) => {
		if(data.status === 'pickedup') {
			setVsatatus(true);
		} else {
			setVsatatus(false)
		}
		dispatch(getPropertyBankVerifyMessagessAction('filter_bankVerificationId='+data._id));
		setVerifyId(data._id)
		setShow(true)
	}

	const getStatusColor = (status) => {
		switch(status) {
			case 'pending': return 'warning';
			case 'pickedup': return 'info';
			case 'verified': return 'success';
			case 'rejected': return 'danger';
			default: return 'secondary';
		}
	};

	// Preprocess data for export to handle N/A values
	const processedExportData = propertyBankVerifyListRows.map(item => {
		const property = item.propertyId || {};
		const customer = item.userId || {};
		return {
			propertyTitle: property.title || '',
			propertyAddress: property.address || '',
			propertyPrice: property.price || '',
			customerName: customer.name || '',
			customerEmail: customer.email || ' ',
			customerPhone: customer.phone || ' ',
			status: item.status || '',
			agentId: item.agentId || '',
			bankId: item.bankId || '',
			createdAt: item.createdAt || '',
			updatedAt: item.updatedAt || ''
		};
	});

	useEffect(() => {
		const filterQuery = buildFilterQuery();
		dispatch(getPropertyBankVerifyListsAction(currentPage, pageSize, '', filterQuery));
	}, [dispatch, currentPage, pageSize, buildFilterQuery]);

	return (
		<>
			<MessageThread
				show={show}
				handleClose={() => setShow(false)}
				onSend={handleSend}
				status={vStatus}
				messages={propertyBankVerifyMessageRows}
			/>

			<div className="form-head page-titles d-flex align-items-center">
				<div className="me-auto d-lg-block">
					<h4 className="mb-1">
						{isPending ? 'Pending' : isCompleted ? 'Completed' : 'In-Progress'} Bank Verification List
					</h4>
				</div>
			</div>
			
			<VerificationFilter 
				key={location.pathname} // Force re-render when path changes
				setOpenMenu={setOpenMenu}
				openMenu={openMenu}
				onSubmit={onSubmit}
				startDate={startDate}
				endDate={endDate}
				setDateRange={setDateRange}
				setCustomer={setCustomer}
				customer={customer}
				isFilterActive={isFilterActive}
				handleClearFilter={handleClearFilter}
				resetKey={location.pathname}
			/>

			<div className="d-flex justify-content-between align-items-center mb-3">
				<PageSizeSelector
					pageSize={pageSize}
					setPageSize={setPageSize}
					setCurrentPage={setCurrentPage}
					options={[12, 24, 50, 100]}
				/>
				<div className="d-flex align-items-center gap-2">
					<CommonExportButtons 
						data={processedExportData} 
						exportHeaders={[
							{ label: 'Property Title', key: 'propertyTitle' },
							{ label: 'Property Address', key: 'propertyAddress' },
							{ label: 'Property Price', key: 'propertyPrice' },
							{ label: 'Customer Name', key: 'customerName' },
							{ label: 'Customer Email', key: 'customerEmail' },
							{ label: 'Customer Phone', key: 'customerPhone' },
							{ label: 'Status', key: 'status' },
							{ label: 'Agent ID', key: 'agentId' },
							{ label: 'Bank ID', key: 'bankId' },
							{ label: 'Created At', key: 'createdAt' },
							{ label: 'Updated At', key: 'updatedAt' }
						]}
						filename={`bank-verification-${isPending ? 'pending' : isCompleted ? 'completed' : 'in-progress'}`}
						pdfTitle={`${isPending ? 'Pending' : isCompleted ? 'Completed' : 'In-Progress'} Bank Verification List`}
						totalText="Total Bank Verifications"
						pdfOrientation="landscape"
						pdfHeaderColor={[52, 144, 220]}
						includeTimestamp={true}
						dateFields={['createdAt', 'updatedAt']}
					/>
					<ViewToggle viewMode={viewMode} onViewChange={handleViewChange} />
				</div>
			</div>

			{!loading && propertyBankVerifyListRows.length === 0 ? (
				<NoDataFound 
					message="No Data Found" 
					description="No bank verification records found for this category." 
				/>
			) : viewMode === 'grid' ? (
				<BankVerificationGridView 
					propertyBankVerifyListRows={propertyBankVerifyListRows}
					loading={loading}
					isPending={isPending}
					isCompleted={isCompleted}
					handleStatusChange={handleStatusChange}
					viewDocument={viewDocument}
					getStatusColor={getStatusColor}
				/>
			) : (
				<BankVerificationListView 
					propertyBankVerifyListRows={propertyBankVerifyListRows}
					loading={loading}
					isPending={isPending}
					isCompleted={isCompleted}
					handleStatusChange={handleStatusChange}
					viewDocument={viewDocument}
					getStatusColor={getStatusColor}
					onSort={handleSort}
					sortField={sortField}
					sortDirection={sortDirection}
				/>
			)}

			{!loading && propertyBankVerifyListRows.length > 0 && (
				<TableFooter
					currentPage={currentPage}
					totalPages={totalPages}
					pageSize={pageSize}
					totalEntries={propertyBankVerifyLists?.total || 0}
					onPageChange={setCurrentPage}
				/>
			)}
		</>
	);
};

export default BankVerficationList;
